import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Policy as PolicyIcon,
  Water as WaterIcon,
  Agriculture as Farm,
  Agriculture,
  ExpandMore,
  Visibility,
  Calculate,
  AttachMoney,
  Refresh,
  Schedule,
  CheckCircle,
  Warning,
  Lock,
  ContactSupport,
  PersonAdd,
  Info,
  Business,
  Close,
  Description,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { policiesAPI, pondsAPI, farmsAPI, stockingEventsAPI } from 'services/api';
import AICPolicyForm from 'shared/components/AICPolicyForm';
import { useNavigate } from 'react-router-dom';

const PolicyManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [ponds, setPonds] = useState([]);
  const [farms, setFarms] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAICDialog, setOpenAICDialog] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [premiumCalculation, setPremiumCalculation] = useState(null);
  const [renewalDialog, setRenewalDialog] = useState(false);
  const [renewingPolicy, setRenewingPolicy] = useState(null);
  const [renewalData, setRenewalData] = useState({
    new_doc_requested: false,
    notes: ''
  });
  const [formData, setFormData] = useState({
    pond_id: '',
    species: 'vannamei',
    insurance_plan: 'A',
    coverage_amount: '',
    start_date: '',
    end_date: '',
    notes: '',
    stocking_event_id: '', // Selected stocking event ID
  });
  const [stockingEvents, setStockingEvents] = useState([]);
  const [selectedStockingEvent, setSelectedStockingEvent] = useState(null);
  const [loadingStockingEvents, setLoadingStockingEvents] = useState(false);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) {
      console.warn('User not available, skipping data load');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [policiesResponse, farmsResponse] = await Promise.all([
        policiesAPI.getAll(),
        farmsAPI.getAll()
      ]);

      // Filter data for current user
      const userFarms = farmsResponse.data.filter(farm => farm.user_id === user.id);
      const userPonds = await pondsAPI.getAllForUser();
      const userPolicies = policiesResponse.data.filter(policy => 
        userPonds.some(pond => pond.id === policy.pond_id)
      );

      setPolicies(userPolicies);
      setPonds(userPonds);
      setFarms(userFarms);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (policy = null) => {
    if (policy) {
      setEditingPolicy(policy);
      setFormData({
        pond_id: policy.pond_id || '',
        species: policy.species || 'vannamei',
        insurance_plan: policy.insurance_plan || 'A',
        coverage_amount: policy.coverage_amount || '',
        start_date: policy.start_date ? policy.start_date.split('T')[0] : '',
        end_date: policy.end_date ? policy.end_date.split('T')[0] : '',
        notes: policy.notes || '',
      });
    } else {
      setEditingPolicy(null);
      setFormData({
        pond_id: '',
        species: 'vannamei',
        insurance_plan: 'A',
        coverage_amount: '',
        start_date: '',
        end_date: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPolicy(null);
    setPremiumCalculation(null);
    setFormData({
      pond_id: '',
      species: 'vannamei',
      insurance_plan: 'A',
      coverage_amount: '',
      start_date: '',
      end_date: '',
      notes: '',
      stocking_event_id: '',
    });
    setStockingEvents([]);
    setSelectedStockingEvent(null);
  };

  const calculatePremium = async () => {
    if (!formData.pond_id || !formData.coverage_amount) {
      setError('Please select a pond and enter coverage amount');
      return;
    }

    try {
      setError(null);
      const calculationData = {
        pond_id: parseInt(formData.pond_id),
        species: formData.species,
        insurance_plan: formData.insurance_plan,
        coverage_amount: parseFloat(formData.coverage_amount),
      };

      const response = await policiesAPI.calculatePremium(calculationData);
      setPremiumCalculation(response.data);
    } catch (err) {
      console.error('Error calculating premium:', err);
      setError('Failed to calculate premium. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      
      const policyData = {
        ...formData,
        pond_id: parseInt(formData.pond_id),
        coverage_amount: parseFloat(formData.coverage_amount),
        premium_amount: premiumCalculation?.premium_amount || 0,
      };

      if (editingPolicy) {
        await policiesAPI.update(editingPolicy.id, policyData);
      } else {
        await policiesAPI.create(policyData);
      }

      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Error saving policy:', err);
      setError('Failed to save policy. Please try again.');
    }
  };

  const handleRenewal = async () => {
    try {
      if (!renewingPolicy) return;
      
      setError(null);
      await policiesAPI.renew(renewingPolicy.id, renewalData.new_doc_requested);
      
      setRenewalDialog(false);
      setRenewingPolicy(null);
      setRenewalData({ new_doc_requested: false, notes: '' });
      loadData();
      
      setError('Policy renewal request submitted successfully!');
    } catch (err) {
      console.error('Error renewing policy:', err);
      setError('Failed to renew policy. Please try again.');
    }
  };

  const isRenewable = (policy) => {
    if (policy.status !== 'active') return false;
    const endDate = new Date(policy.policy_end_date);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  };

  const getPolicyStatusChip = (policy) => {
    const endDate = new Date(policy.policy_end_date);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    
    if (policy.status === 'pending') {
      return <Chip label="Pending Approval" color="warning" size="small" icon={<Schedule />} />;
    } else if (policy.status === 'active') {
      if (daysUntilExpiry <= 7) {
        return <Chip label={`Expires in ${daysUntilExpiry} days`} color="error" size="small" icon={<Warning />} />;
      } else if (daysUntilExpiry <= 30) {
        return <Chip label={`Expires in ${daysUntilExpiry} days`} color="warning" size="small" icon={<Warning />} />;
      } else {
        return <Chip label="Active" color="success" size="small" icon={<CheckCircle />} />;
      }
    } else if (policy.status === 'expired') {
      return <Chip label="Expired" color="default" size="small" />;
    } else {
      return <Chip label={policy.status} color="default" size="small" />;
    }
  };

  const handleDelete = async (policyId) => {
    if (window.confirm('Are you sure you want to delete this policy?')) {
      try {
        await policiesAPI.delete(policyId);
        loadData();
      } catch (err) {
        console.error('Error deleting policy:', err);
        setError('Failed to delete policy. Please try again.');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'expired': return 'error';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getPlanColor = (plan) => {
    switch (plan) {
      case 'A': return 'primary';
      case 'B': return 'secondary';
      case 'C': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        
        {policies.length === 0 ? (
          <Tooltip title="First-time policy creation requires assistance from an insurance officer">
            <span>
              <Button
                variant="outlined"
                startIcon={<Lock />}
                disabled
                sx={{ 
                  bgcolor: 'grey.100',
                  '&:disabled': {
                    bgcolor: 'grey.100',
                    color: 'grey.600',
                    border: '1px solid #ccc'
                  }
                }}
              >
                Create New Policy
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              color="primary"
            >
              Create New Policy
            </Button>
            <Button
              variant="outlined"
              startIcon={<Business />}
              onClick={() => setOpenAICDialog(true)}
              color="secondary"
              sx={{ 
                borderColor: 'secondary.main',
                color: 'secondary.main',
                '&:hover': {
                  borderColor: 'secondary.dark',
                  backgroundColor: 'secondary.light',
                  color: 'secondary.dark'
                }
              }}
            >
              AIC Policy Form
            </Button>
          </Box>
        )}
      </Box>

      {policies.length === 0 && (
        <Alert 
          severity="info" 
          action={
            <Button 
              color="inherit" 
              size="small" 
              startIcon={<ContactSupport />}
              onClick={() => window.open('tel:+911800123456', '_self')}
            >
              Contact Officer
            </Button>
          }
          sx={{ mb: 3 }}
        >
          <strong>Need Your First Policy?</strong> For first-time policy creation, please contact our insurance officer 
          who will guide you through the process and create your initial policy. After that, you can create additional policies yourself.
          <br />
          <Typography variant="body2" sx={{ mt: 1 }}>
            📞 Insurance Officer: +91 1800-123-456 | 📧 officer@aic-india.com
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Policies
                  </Typography>
                  <Typography variant="h4" component="div" color="primary">
                    {policies.length}
                  </Typography>
                </Box>
                <PolicyIcon fontSize="large" color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Active Policies
                  </Typography>
                  <Typography variant="h4" component="div" color="success.main">
                    {policies.filter(p => p.status === 'active').length}
                  </Typography>
                </Box>
                <PolicyIcon fontSize="large" color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Pending Approval
                  </Typography>
                  <Typography variant="h4" component="div" color="warning.main">
                    {policies.filter(p => p.status === 'pending').length}
                  </Typography>
                </Box>
                <PolicyIcon fontSize="large" color="warning" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Coverage
                  </Typography>
                  <Typography variant="h4" component="div" color="info.main">
                    ₹{policies.reduce((sum, policy) => sum + (policy.coverage_amount || 0), 0).toFixed(2)}
                  </Typography>
                </Box>
                <AttachMoney fontSize="large" color="info" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Policies List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your Insurance Policies
          </Typography>
          {policies.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PolicyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No policies created yet
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Create your first insurance policy to protect your shrimp farming investment
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                sx={{ mt: 2 }}
              >
                Create Your First Policy
              </Button>
            </Box>
          ) : (
            <Box>
              {policies.map((policy) => {
                const pond = ponds.find(p => p.id === policy.pond_id);
                const farm = farms.find(f => f.id === pond?.farm_id);
                
                return (
                  <Accordion key={policy.id} sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <PolicyIcon sx={{ mr: 2, color: 'primary.main' }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6">
                            Policy #{policy.id} - {pond?.name || 'Unknown Pond'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {farm?.name} • {policy.species} • Plan {policy.insurance_plan}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                          {getPolicyStatusChip(policy)}
                          <Chip
                            label={`Plan ${policy.insurance_plan}`}
                            color={getPlanColor(policy.insurance_plan)}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Policy Details
                          </Typography>
                          <Typography variant="body2">
                            <strong>Species:</strong> {policy.species}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Coverage Amount:</strong> ₹{policy.coverage_amount ? parseFloat(policy.coverage_amount).toFixed(2) : '0.00'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Premium Amount:</strong> ₹{policy.premium_amount ? parseFloat(policy.premium_amount).toFixed(2) : '0.00'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Start Date:</strong> {new Date(policy.start_date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2">
                            <strong>End Date:</strong> {new Date(policy.end_date).toLocaleDateString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Pond & Farm Information
                          </Typography>
                          <Typography variant="body2">
                            <strong>Farm:</strong> {farm?.name}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Pond:</strong> {pond?.name}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Pond Area:</strong> {pond?.area ? parseFloat(pond.area).toFixed(2) : 'N/A'} hectares
                          </Typography>
                          <Typography variant="body2">
                            <strong>Stocking Density:</strong> {pond?.stocking_density ? parseFloat(pond.stocking_density).toFixed(2) : 'N/A'} per m²
                          </Typography>
                        </Grid>
                        {policy.notes && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                              Notes
                            </Typography>
                            <Typography variant="body2">
                              {policy.notes}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => navigate(`/farmer/policies/${policy.id}`)}
                            >
                              View Details
                            </Button>
                            {isRenewable(policy) && (
                              <Button
                                size="small"
                                startIcon={<Refresh />}
                                onClick={() => {
                                  setRenewingPolicy(policy);
                                  setRenewalDialog(true);
                                }}
                                color="warning"
                                variant="outlined"
                              >
                                Renew
                              </Button>
                            )}
                            <Button
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => handleOpenDialog(policy)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              startIcon={<Delete />}
                              onClick={() => handleDelete(policy.id)}
                              color="error"
                            >
                              Delete
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Professional Add/Edit Policy Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            maxHeight: '95vh'
          }
        }}
      >
        {/* Enhanced Header */}
        <DialogTitle sx={{ 
          pb: 1.5, 
          pt: 2,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 1.5, 
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  mr: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PolicyIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 600, mb: 0.25 }}>
                    {editingPolicy ? 'Edit Policy' : 'Create New Policy'}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {editingPolicy ? 'Update policy information' : 'Set up insurance coverage for your operation'}
                  </Typography>
                </Box>
              </Box>
              <IconButton 
                onClick={handleCloseDialog}
                size="small"
                sx={{ 
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 0, bgcolor: '#fafafa', maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <Box sx={{ p: 2.5 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Pond & Species Selection Card */}
              <Card sx={{ 
                mb: 2, 
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid #e3f2fd',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                position: 'relative',
                zIndex: 1
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1.5, 
                      bgcolor: 'primary.main',
                      mr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <WaterIcon sx={{ color: 'white', fontSize: 18 }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Pond & Species Information
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required error={!formData.pond_id} size="small">
                        <InputLabel>Select Pond *</InputLabel>
                        <Select
                          value={formData.pond_id}
                          onChange={async (e) => {
                            const pondId = e.target.value;
                            setFormData({ ...formData, pond_id: pondId, stocking_event_id: '' });
                            setSelectedStockingEvent(null);
                            
                            // Load stocking events for the selected pond
                            if (pondId) {
                              setLoadingStockingEvents(true);
                              try {
                                const response = await stockingEventsAPI.getByPond(pondId);
                                const events = response.data || [];
                                setStockingEvents(events);
                                
                                // Auto-select the most recent stocking event if only one exists
                                if (events.length === 1) {
                                  setSelectedStockingEvent(events[0]);
                                  setFormData(prev => ({ ...prev, stocking_event_id: events[0].id }));
                                } else if (events.length > 1) {
                                  // If multiple events, don't auto-select, let user choose
                                  setSelectedStockingEvent(null);
                                }
                              } catch (err) {
                                console.error('Error loading stocking events:', err);
                                setStockingEvents([]);
                              } finally {
                                setLoadingStockingEvents(false);
                              }
                            } else {
                              setStockingEvents([]);
                            }
                          }}
                          label="Select Pond *"
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 300,
                                zIndex: 9999
                              }
                            }
                          }}
                        >
                          {ponds.map((pond) => {
                            const farm = farms.find(f => f.id === pond.farm_id);
                            return (
                              <MenuItem key={pond.id} value={pond.id}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {pond.pond_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {farm?.name} • {pond.pond_area} ha • {pond.species}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </Select>
                        {!formData.pond_id && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5, display: 'block' }}>
                            Please select a pond
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required size="small" sx={{ width: '100%' }}>
                        <InputLabel id="species-label">Primary Species *</InputLabel>
                        <Select
                          labelId="species-label"
                          value={formData.species}
                          onChange={(e) => setFormData({ ...formData, species: e.target.value })}
                          label="Primary Species *"
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 300,
                                zIndex: 9999
                              }
                            }
                          }}
                          sx={{
                            width: '100%',
                            '& .MuiSelect-select': {
                              width: '100%',
                              minWidth: '180px'
                            }
                          }}
                        >
                          <MenuItem value="Vannamei">
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>Vannamei (White Shrimp)</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Most common, high yield
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="Monodon">
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>Monodon (Tiger Shrimp)</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Premium quality, higher value
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="Scampi">
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>Scampi (Freshwater Prawn)</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Freshwater species
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="Indicus">
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>Indicus (Indian Shrimp)</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Native, disease resistant
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Stocking Event Information Card */}
              {formData.pond_id && (
                <Card sx={{ 
                  mb: 2, 
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid #e3f2fd',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Box sx={{ 
                        p: 1, 
                        borderRadius: 1.5, 
                        bgcolor: 'info.main',
                        mr: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Agriculture sx={{ color: 'white', fontSize: 18 }} />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'info.main' }}>
                        Stocking Event Information
                      </Typography>
                    </Box>

                    {loadingStockingEvents ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : stockingEvents.length === 0 ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          No stocking events found for this pond. A stocking event is required before creating a policy.
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          Please create a stocking event in the "Stocking Details" page or "Ponds" management page first.
                        </Typography>
                      </Alert>
                    ) : stockingEvents.length === 1 ? (
                      <Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Using Stocking Event:</strong> {new Date(stockingEvents[0].stocking_date).toLocaleDateString()}
                          </Typography>
                        </Alert>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Stocking Date</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {new Date(stockingEvents[0].stocking_date).toLocaleDateString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Species</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {stockingEvents[0].species || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Stocking Density</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {stockingEvents[0].stocking_density_per_m2 || stockingEvents[0].stocking_density || 'N/A'} per m²
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">SPF Seed</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {stockingEvents[0].spf_seed ? 'Yes' : 'No'}
                            </Typography>
                          </Grid>
                          {stockingEvents[0].stocking_date && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Days of Culture (DOC)</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {Math.floor((new Date() - new Date(stockingEvents[0].stocking_date)) / (1000 * 60 * 60 * 24))} days
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    ) : (
                      <Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Multiple Stocking Events Found:</strong> Please select which stocking event to use for this policy.
                          </Typography>
                        </Alert>
                        <FormControl fullWidth required size="small" sx={{ mb: 2 }}>
                          <InputLabel>Select Stocking Event *</InputLabel>
                          <Select
                            value={formData.stocking_event_id || ''}
                            onChange={(e) => {
                              const eventId = e.target.value;
                              const event = stockingEvents.find(se => se.id === parseInt(eventId));
                              setFormData({ ...formData, stocking_event_id: eventId });
                              setSelectedStockingEvent(event);
                            }}
                            label="Select Stocking Event *"
                          >
                            {stockingEvents.map((event) => (
                              <MenuItem key={event.id} value={event.id}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {new Date(event.stocking_date).toLocaleDateString()} - {event.species || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Density: {event.stocking_density_per_m2 || event.stocking_density || 'N/A'}/m² • 
                                    SPF: {event.spf_seed ? 'Yes' : 'No'} • 
                                    DOC: {event.stocking_date ? Math.floor((new Date() - new Date(event.stocking_date)) / (1000 * 60 * 60 * 24)) : 'N/A'} days
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {selectedStockingEvent && (
                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Stocking Date</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {new Date(selectedStockingEvent.stocking_date).toLocaleDateString()}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Species</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {selectedStockingEvent.species || 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Stocking Density</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {selectedStockingEvent.stocking_density_per_m2 || selectedStockingEvent.stocking_density || 'N/A'} per m²
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">SPF Seed</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {selectedStockingEvent.spf_seed ? 'Yes' : 'No'}
                              </Typography>
                            </Grid>
                            {selectedStockingEvent.stocking_date && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">Days of Culture (DOC)</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {Math.floor((new Date() - new Date(selectedStockingEvent.stocking_date)) / (1000 * 60 * 60 * 24))} days
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Insurance Coverage Card */}
              <Card sx={{ 
                mb: 2, 
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid #e3f2fd',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                position: 'relative',
                zIndex: 1
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1.5, 
                      bgcolor: 'success.main',
                      mr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <AttachMoney sx={{ color: 'white', fontSize: 18 }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'success.main' }}>
                      Insurance Coverage Details
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required size="small">
                        <InputLabel>Insurance Plan *</InputLabel>
                        <Select
                          value={formData.insurance_plan}
                          onChange={(e) => setFormData({ ...formData, insurance_plan: e.target.value })}
                          label="Insurance Plan *"
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 300,
                                zIndex: 9999
                              }
                            }
                          }}
                        >
                          <MenuItem value="A">
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>Plan A - Basic Coverage</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Essential protection, lower premium
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="B">
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>Plan B - Standard Coverage</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Balanced protection and cost
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="C">
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>Plan C - Premium Coverage</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Comprehensive protection, higher premium
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Coverage Amount (₹) *"
                        type="number"
                        value={formData.coverage_amount}
                        onChange={(e) => setFormData({ ...formData, coverage_amount: e.target.value })}
                        placeholder="Enter coverage amount"
                        required
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Policy Start Date *"
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Policy End Date *"
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Premium Calculation Card */}
              {formData.pond_id && formData.coverage_amount && (
                <Card sx={{ 
                  mb: 2, 
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid #e3f2fd',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Box sx={{ 
                        p: 1, 
                        borderRadius: 1.5, 
                        bgcolor: 'warning.main',
                        mr: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Calculate sx={{ color: 'white', fontSize: 18 }} />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'warning.main' }}>
                        Premium Calculation
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Calculate />}
                        onClick={calculatePremium}
                        sx={{ borderRadius: 1.5 }}
                      >
                        Calculate Premium
                      </Button>
                    </Box>
                    
                    {premiumCalculation && (
                      <Card sx={{ bgcolor: 'primary.50', border: '1px solid #e3f2fd' }}>
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', mb: 1.5 }}>
                            Premium Breakdown
                          </Typography>
                          <Grid container spacing={1.5}>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1.5, border: '1px solid #e3f2fd' }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  Base Premium
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  ₹{premiumCalculation.base_premium?.toLocaleString()}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1.5, border: '1px solid #e3f2fd' }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  Species Loading
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  {premiumCalculation.species_loading}%
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1.5, border: '1px solid #e3f2fd' }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  Plan Loading
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  {premiumCalculation.plan_loading}%
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ p: 1.5, bgcolor: 'primary.main', borderRadius: 1.5, color: 'white' }}>
                                <Typography variant="caption" display="block" sx={{ mb: 0.5, opacity: 0.9 }}>
                                  Total Premium
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  ₹{premiumCalculation.premium_amount ? parseFloat(premiumCalculation.premium_amount).toFixed(2) : '0.00'}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Additional Notes Card - Optional */}
              <Card sx={{ 
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid #e3f2fd',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                position: 'relative',
                zIndex: 1
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1.5, 
                      bgcolor: 'info.main',
                      mr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Description sx={{ color: 'white', fontSize: 18 }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'info.main' }}>
                      Additional Information (Optional)
                    </Typography>
                  </Box>
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Notes"
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Add any additional notes or special requirements (optional)..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </CardContent>
              </Card>
            </Box>
          </DialogContent>

          {/* Enhanced Footer */}
          <DialogActions sx={{ 
            px: 2.5, 
            py: 2, 
            bgcolor: '#f8f9fa',
            borderTop: '1px solid #e3f2fd'
          }}>
            <Box sx={{ display: 'flex', gap: 1.5, width: '100%', justifyContent: 'flex-end' }}>
              <Button 
                onClick={handleCloseDialog}
                variant="outlined"
                size="small"
                sx={{ 
                  minWidth: 100,
                  px: 2,
                  py: 1,
                  borderRadius: 1.5,
                  fontWeight: 600,
                  borderColor: 'grey.300',
                  color: 'grey.700',
                  '&:hover': {
                    borderColor: 'grey.400',
                    bgcolor: 'grey.50'
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                variant="contained"
                color="primary"
                size="small"
                sx={{ 
                  minWidth: 140,
                  px: 2.5,
                  py: 1,
                  borderRadius: 1.5,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)'
                  }
                }}
              >
                {editingPolicy ? 'Update Policy' : 'Create Policy'}
              </Button>
            </Box>
          </DialogActions>
        </form>
      </Dialog>

      {/* Policy Renewal Dialog */}
      <Dialog open={renewalDialog} onClose={() => setRenewalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Renew Policy
        </DialogTitle>
        <DialogContent>
          {renewingPolicy && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Policy Details
                </Typography>
                <Typography variant="body2">
                  <strong>Policy Number:</strong> #{renewingPolicy.id}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Plan:</strong> {renewingPolicy.insurance_plan}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Premium:</strong> ₹{renewingPolicy.final_premium?.toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Expiry Date:</strong> {new Date(renewingPolicy.policy_end_date).toLocaleDateString()}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Renewal Options</InputLabel>
                  <Select
                    value={renewalData.new_doc_requested ? 'new_doc' : 'same_doc'}
                    onChange={(e) => setRenewalData({
                      ...renewalData,
                      new_doc_requested: e.target.value === 'new_doc'
                    })}
                    label="Renewal Options"
                  >
                    <MenuItem value="same_doc">
                      Renew with Same Documentation Period
                    </MenuItem>
                    <MenuItem value="new_doc">
                      Request New Documentation Period
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Renewal Notes (Optional)"
                  multiline
                  rows={3}
                  value={renewalData.notes}
                  onChange={(e) => setRenewalData({
                    ...renewalData,
                    notes: e.target.value
                  })}
                  placeholder="Add any notes for the renewal request..."
                />
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Renewal Process:</strong>
                  </Typography>
                  <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
                    <li>Your renewal request will be submitted for admin approval</li>
                    <li>New premium will be calculated based on current rates</li>
                    <li>Policy will be extended for the next cycle upon approval</li>
                    <li>You will be notified of the approval status via email</li>
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewalDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleRenewal} 
            variant="contained"
            color="warning"
            startIcon={<Refresh />}
          >
            Submit Renewal Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* AIC Policy Form Dialog */}
      <Dialog
        open={openAICDialog}
        onClose={() => setOpenAICDialog(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            overflow: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Business sx={{ mr: 1 }} />
            Agriculture Insurance Company of India Ltd.
          </Box>
          <IconButton 
            onClick={() => setOpenAICDialog(false)}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <AICPolicyForm
            onSubmit={async (formData) => {
              try {
                // Handle AIC form submission
                console.log('AIC Form Data:', formData);
                setOpenAICDialog(false);
                // You can add API call here to save the AIC form data
                setError('AIC Policy form submitted successfully!');
              } catch (err) {
                console.error('Error submitting AIC form:', err);
                setError('Failed to submit AIC form. Please try again.');
              }
            }}
            loading={false}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PolicyManagement;
